import React, { useState } from "react";
import {
  CaretLeft,
  CaretDown,
  CaretUp,
  ShieldCheck,
  ArrowSquareOut,
  Drop,
  Briefcase,
  Suitcase,
  Prohibit,
  HandWaving,
  Lightning,
} from "@phosphor-icons/react";
import BrandMark from "./BrandMark";
import AccountButton from "../AccountButton";

/**
 * TSA Rules — static curated reference card for travelers (US TSA).
 *
 * Content sourced from public TSA.gov guidelines (last verified Feb 2026).
 * Designed to load instantly with zero network calls. A "View on TSA.gov"
 * deep-link is provided in each section for the authoritative live source.
 */
const SECTIONS = [
  {
    id: "liquids",
    title: "3-1-1 liquid rule",
    summary: "Carry-on liquids must be ≤ 3.4 oz (100 ml) per container.",
    Icon: Drop,
    color: "#2A6FD8",
    soft: "#EAF2FB",
    body: [
      "All liquids, gels, aerosols, creams and pastes must be in containers of 3.4 oz (100 ml) or less.",
      "All containers must fit in a single, clear, quart-sized zip-top bag — one bag per traveler.",
      "Remove the bag from your carry-on and place it in the bin at the checkpoint.",
      "Exemptions: medications, baby formula/food, breast milk — declare these to the TSA officer.",
    ],
    link: "https://www.tsa.gov/travel/security-screening/liquids-rule",
  },
  {
    id: "carryon",
    title: "Carry-on essentials",
    summary: "Personal item + 1 carry-on. Laptop & electronics out at screening.",
    Icon: Briefcase,
    color: "#E55B2C",
    soft: "#FDE6D8",
    body: [
      "Most airlines allow 1 carry-on bag + 1 personal item (purse, backpack).",
      "Laptops, tablets, and large electronics must come out and be placed in their own bin (unless you have TSA PreCheck®).",
      "Power banks and lithium-ion batteries: MUST go in carry-on. Never in checked baggage.",
      "Liquids over 3.4 oz, sharp objects, and lighters (other than 1 disposable per person) belong in checked bags.",
    ],
    link: "https://www.tsa.gov/travel/security-screening",
  },
  {
    id: "checked",
    title: "Checked baggage",
    summary: "Most items allowed. NO lithium batteries, e-cigs, or flammables.",
    Icon: Suitcase,
    color: "#2E7D5A",
    soft: "#DCEFE3",
    body: [
      "Never pack: spare lithium-ion batteries, vape pens / e-cigarettes, fuel, flammables, or matches.",
      "Firearms must be unloaded, in a locked hard-sided case, declared at the airline counter.",
      "Use a TSA-approved (Travel Sentry) lock so agents can re-lock after inspection.",
      "Tag your bag inside AND outside — exterior tags can fall off.",
    ],
    link: "https://www.tsa.gov/travel/security-screening/whatcanibring/all",
  },
  {
    id: "prohibited",
    title: "Always prohibited",
    summary: "Box cutters, large knives, gel-type ice packs, bear spray.",
    Icon: Prohibit,
    color: "#C94D23",
    soft: "#FDE6D8",
    body: [
      "Box cutters, straight razors, large knives (over 2.36\" / 6 cm).",
      "Self-defense sprays, mace, pepper spray > 4 fl oz, bear spray.",
      "Realistic replica firearms or grenades — even toys.",
      "Flammable items, fireworks, gunpowder.",
      "Recreational drugs (including federally-prohibited marijuana, even from legal states).",
    ],
    link: "https://www.tsa.gov/travel/security-screening/whatcanibring/all",
  },
  {
    id: "checkpoint",
    title: "At the checkpoint",
    summary: "Have ID + boarding pass ready. Shoes/belt off (unless PreCheck).",
    Icon: HandWaving,
    color: "#7B5BD8",
    soft: "#EDE7FB",
    body: [
      "Have your government-issued ID and boarding pass ready before reaching the officer.",
      "REAL ID enforcement: as of May 7, 2025 you need REAL ID-compliant ID (or passport) for domestic flights.",
      "Standard screening: remove shoes, belts, jackets, and large electronics from bags.",
      "TSA PreCheck® travelers keep shoes, belt, and light jacket on, and laptop in bag.",
      "If you opt out of body-scanner: politely ask for a private screening — it's your right.",
    ],
    link: "https://www.tsa.gov/precheck",
  },
  {
    id: "tips",
    title: "Speed-through tips",
    summary: "PreCheck cuts wait time. Arrive 2 hr domestic / 3 hr international.",
    Icon: Lightning,
    color: "#E55B2C",
    soft: "#FDE6D8",
    body: [
      "TSA PreCheck® ($78 / 5 yrs) — faster lane, shoes/belt/laptop stay in. Global Entry ($120 / 5 yrs) includes PreCheck + expedited US re-entry.",
      "Avoid surge times: Sundays 5–8 PM, Monday early AM, Friday 4–7 PM, holiday eves.",
      "Use the MyTSA app for live wait-time predictions at your airport.",
      "Wear slip-on shoes and minimal metal. Pack liquids in the OUTER pocket so the bag pulls out fast.",
    ],
    link: "https://www.tsa.gov/travel/travel-tips",
  },
];

export default function TSARulesPage({ openPaywall, onBack }) {
  const [openId, setOpenId] = useState("liquids");

  return (
    <div className="px-5 pt-4 pb-2" data-testid="tsa-rules-screen">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => onBack?.()}
          data-testid="tsa-back"
          aria-label="Back"
          className="w-10 h-10 rounded-full bg-white shadow-soft border border-navy/10 flex items-center justify-center"
        >
          <CaretLeft size={20} weight="bold" color="#0E2C54" />
        </button>
        <BrandMark />
        <AccountButton openPaywall={() => openPaywall?.()} />
      </div>

      {/* Hero */}
      <div className="bg-navy text-white rounded-2xl p-5 mb-4 flex items-center gap-4 shadow-float">
        <div className="shrink-0 w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
          <ShieldCheck size={32} weight="fill" color="#5DD16F" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-jakarta font-extrabold text-2xl leading-tight">
            TSA Rules
          </h1>
          <p className="text-white/75 text-sm font-jakarta font-medium leading-snug mt-0.5">
            Quick, current US TSA guidance for breezing through security.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {SECTIONS.map((s) => {
          const open = openId === s.id;
          return (
            <div
              key={s.id}
              data-testid={`tsa-section-${s.id}`}
              className="bg-white rounded-2xl shadow-soft border border-navy/5 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenId(open ? null : s.id)}
                className="w-full px-4 py-3.5 flex items-center gap-3 text-left"
              >
                <div
                  className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: s.soft }}
                >
                  <s.Icon size={22} weight="duotone" color={s.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-jakarta font-extrabold text-navy text-base leading-tight">
                    {s.title}
                  </div>
                  <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5 leading-snug">
                    {s.summary}
                  </div>
                </div>
                <div className="shrink-0">
                  {open ? (
                    <CaretUp size={18} weight="bold" color="#4A5D78" />
                  ) : (
                    <CaretDown size={18} weight="bold" color="#4A5D78" />
                  )}
                </div>
              </button>

              {open && (
                <div className="px-4 pb-4 -mt-1">
                  <ul className="space-y-2">
                    {s.body.map((line, i) => (
                      <li
                        key={i}
                        className="text-navy text-sm font-jakarta font-medium leading-relaxed flex gap-2"
                      >
                        <span
                          className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: s.color }}
                        />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-orange font-jakarta font-bold text-sm"
                  >
                    View on TSA.gov
                    <ArrowSquareOut size={14} weight="bold" />
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-center text-navy-soft text-xs font-jakarta font-medium mt-5 leading-relaxed">
        Last verified Feb 2026 against TSA.gov. Always check the official site
        before flying — rules change.
      </p>
    </div>
  );
}
